import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import DocumentHead from './components/Layout/DocumentHead';

// Page Components
import LandingPage from './pages/Landing/LandingPage';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Members from './pages/Members/Members';
import MemberDetail from './pages/Members/MemberDetail';
import AdminMemberForm from './pages/Members/AdminMemberForm';
import MemberApplicationView from './pages/Members/MemberApplicationView';
import TrainingPrograms from './pages/Training/TrainingPrograms';
import TrainingManagement from './pages/Training/TrainingManagement';
import TrainingRegistrations from './pages/Training/TrainingRegistrations';
import CertificateSettings from './pages/Training/CertificateSettings';
import TrainingEmailTemplates from './pages/Training/TrainingEmailTemplates';
import Events from './pages/Events/Events';
import EventsManagement from './pages/Events/EventsManagement';
import FormBuilder from './pages/FormBuilder/FormBuilder';
import Reports from './pages/Reports/Reports';
import UserManagement from './pages/Admin/UserManagement';
import Settings from './pages/Admin/Settings';
import RolePermissionSetup from './pages/Admin/RolePermissionSetup';
import AuditLogs from './pages/Admin/AuditLogs';
import Profile from './pages/Profile/Profile';
import NewsManagement from './pages/News/NewsManagement';
import NewsPage from './pages/News/NewsPage';
import EventsPage from './pages/Events/EventsPage';
import ResearchPublications from './pages/Publications/ResearchPublications';
import GalleryManagement from './pages/Gallery/GalleryManagement';
import GalleryPage from './pages/Gallery/GalleryPage';
import AlbumMediaManagement from './pages/Gallery/AlbumMediaManagement';
import ContactPage from './pages/Contact/ContactPage';
import PublicationsPage from './pages/Publications/PublicationsPage';
import NewsDetail from './pages/News/NewsDetail';
import TrainingRegistration from './pages/Training/TrainingRegistration';
import AboutPage from './pages/About/AboutPage';
import AboutManagement from './pages/Admin/AboutManagement';
import ContactMessages from './pages/Admin/ContactMessages';
import ContactSettings from './pages/Admin/ContactSettings';
import TestimonialsManagement from './pages/Admin/TestimonialsManagement';
import PartnersManagement from './pages/Admin/PartnersManagement';
import DonationProjectsManagement from './pages/Admin/DonationProjectsManagement';
import DonationBankAccountsManagement from './pages/Admin/DonationBankAccountsManagement';
import DonatePage from './pages/Donate/DonatePage';
import SurveyManagement from './pages/Admin/SurveyManagement';
import SurveyResponses from './pages/Admin/SurveyResponses';
import SurveyPage from './pages/Survey/SurveyPage';
import JobsManagement from './pages/Jobs/JobsManagement';
import JobCreate from './pages/Jobs/JobCreate';
import PublicJobsList from './pages/Jobs/PublicJobsList';
import PublicJobDetail from './pages/Jobs/PublicJobDetail';
import JobApplications from './pages/Jobs/JobApplications';
import JobApplicationsDashboard from './pages/Jobs/JobApplicationsDashboard';
import JobEdit from './pages/Jobs/JobEdit';
import MembershipApplicationFormsList from './pages/MembershipApplications/MembershipApplicationFormsList';
import MembershipApplicationFormBuilder from './pages/MembershipApplications/MembershipApplicationFormBuilder';
import MembershipApplicationFormView from './pages/MembershipApplications/MembershipApplicationFormView';
import MembershipApplicationImport from './pages/MembershipApplications/MembershipApplicationImport';
import PublicMembershipApplication from './pages/MembershipApplications/PublicMembershipApplication';
import PublicMembershipApplicationsList from './pages/MembershipApplications/PublicMembershipApplicationsList';
import MembershipApplicationSubmissions from './pages/MembershipApplications/MembershipApplicationSubmissions';
// DraftMembers removed - direct membership on approval
// import DraftMembers from './pages/Members/DraftMembers';
import CompleteMemberProfile from './pages/Members/CompleteMemberProfile';
import SubmissionDetail from './pages/MembershipApplications/SubmissionDetail';
import EmailSettings from './pages/Admin/EmailSettings';
import ApplicationStatus from './pages/MembershipApplications/ApplicationStatus';
// ApplicationRegistration removed - direct membership on approval
import ChatPage from './pages/Chat/ChatPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <DocumentHead />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/donate" element={<DonatePage />} />
                    <Route path="/publications" element={<PublicationsPage />} />
                    <Route path="/news" element={<NewsPage />} />
                    <Route path="/news/:id" element={<NewsDetail />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/gallery" element={<GalleryPage />} />
                    <Route path="/surveys" element={<SurveyPage />} />
                    <Route path="/surveys/:slug" element={<SurveyPage />} />
                    <Route path="/jobs" element={<PublicJobsList />} />
                    <Route path="/jobs/:id" element={<PublicJobDetail />} />
                    <Route path="/training/:trainingId/register" element={<TrainingRegistration />} />
                    <Route path="/membership-applications" element={<PublicMembershipApplicationsList />} />
                    <Route path="/membership-application/:slug" element={<PublicMembershipApplication />} />
                    <Route path="/application-status" element={<ApplicationStatus />} />
                    
                    {/* Protected Routes */}
                    <Route path="/app" element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }>
                      <Route index element={<Navigate to="/app/dashboard" replace />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      
                      {/* Member Management */}
                      <Route path="members" element={<Members />} />
                      <Route path="members/add" element={<AdminMemberForm />} />
                      {/* Main member view now shows full application-style view */}
                      <Route path="members/:id" element={<MemberApplicationView />} />
                      {/* Optional separate profile view if needed elsewhere */}
                      <Route path="members/:id/profile" element={<MemberDetail />} />
                      
                      {/* Secure Chat */}
                      <Route path="chat" element={<ChatPage />} />
                      
                      {/* Training & Events */}
                      <Route path="training" element={<TrainingPrograms />} />
                      <Route path="training/manage" element={<TrainingManagement />} />
                      <Route path="training/:trainingId/registrations" element={<TrainingRegistrations />} />
                      <Route path="training/certificate-settings" element={<CertificateSettings />} />
                      <Route path="training/email-templates" element={<TrainingEmailTemplates />} />
                      <Route path="events" element={<Events />} />
                      <Route path="events/manage" element={<EventsManagement />} />
                      
                      {/* Content Management */}
                      <Route path="news" element={<NewsManagement />} />
                      <Route path="publications" element={<ResearchPublications />} />
                      <Route path="gallery" element={<GalleryManagement />} />
                      <Route path="gallery/:albumId" element={<AlbumMediaManagement />} />
                      <Route path="about" element={<AboutManagement />} />
                      <Route path="testimonials" element={<TestimonialsManagement />} />
                      <Route path="partners" element={<PartnersManagement />} />
                      <Route path="donation-bank-accounts" element={<DonationBankAccountsManagement />} />
                      {/* Backwards compatibility: redirect old donation-projects route to new donate page */}
                      <Route path="donation-projects" element={<Navigate to="/app/donation-bank-accounts" replace />} />
                      
                      {/* Form Builder */}
                      <Route path="forms" element={<FormBuilder />} />
                      {/* Jobs Management */}
                      <Route path="jobs" element={<JobsManagement />} />
                      <Route path="jobs/create" element={<JobCreate />} />
                      <Route path="jobs/:id/edit" element={<JobEdit />} />
                      <Route path="jobs/:jobId/applications" element={<JobApplications />} />
                      <Route path="jobs/applications/dashboard" element={<JobApplicationsDashboard />} />
                      
                      {/* Membership Applications */}
                      <Route path="membership-applications/forms" element={<MembershipApplicationFormsList />} />
                      <Route path="membership-applications/forms/create" element={<MembershipApplicationFormBuilder />} />
                      <Route path="membership-applications/forms/:id" element={<MembershipApplicationFormView />} />
                      <Route path="membership-applications/import" element={<MembershipApplicationImport />} />
                      <Route path="membership-applications/forms/:id/edit" element={<MembershipApplicationFormBuilder />} />
                      <Route path="membership-applications/submissions" element={<MembershipApplicationSubmissions />} />
                      <Route path="membership-applications/submissions/:id" element={<SubmissionDetail />} />
                      
                      {/* Draft Members - removed, direct membership on approval */}
                      <Route path="members/:id/complete" element={<AdminMemberForm />} />
                      
                      {/* Reports */}
                      <Route path="reports" element={<Reports />} />
                      
                      {/* Admin */}
                      <Route path="admin/users" element={<UserManagement />} />
                      <Route path="admin/settings" element={<Settings />} />
                      <Route path="admin/email-settings" element={<EmailSettings />} />
                      <Route path="admin/roles" element={<RolePermissionSetup />} />
                      <Route path="admin/audit" element={<AuditLogs />} />
                      
                      {/* Contact Management */}
                      <Route path="contact/messages" element={<ContactMessages />} />
                      <Route path="contact/settings" element={<ContactSettings />} />
                      
                      {/* Survey Management */}
                      <Route path="surveys/manage" element={<SurveyManagement />} />
                      <Route path="surveys/:surveyId/responses" element={<SurveyResponses />} />
                      
                      {/* Profile */}
                      <Route path="profile" element={<Profile />} />
                    </Route>
                    
                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
              
              {/* Toast Notifications */}
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
